/**
 * SingleEmailModal
 *
 * A reusable two-step email modal that uses the SendIt MFE in **single** mode.
 *
 * Step 1 – Compose & Recipients: template picker, subject, message body,
 *          recipient configuration (To / CC), attach invoices toggle, file upload.
 * Step 2 – SendIt MFE: embedded MFE in single mode.
 *
 * Callers provide customer/recipient context and optional callbacks for
 * attachment fetching and pre-send logging so the modal stays domain-agnostic.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowRight,
  Loader2,
  AlertCircle,
} from "lucide-react";
import { CompactEmailSelector } from "@/components/shared/bulk-email-modal";
import { toast } from "sonner";
import { EvoMfeApp } from "@access-evo/evo-api";
import { useAuth } from "@/providers/AuthProvider";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import type { Editor } from "@tiptap/react";
import { useTemplatesList, useTemplateById } from "@/hooks/useTemplatesData";
import { useCustomerContactsList } from "@/hooks/useCustomerContacts";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { VariableExtension } from "@/lib/tiptap-variable-extension";
import {
  convertVariablesToNodes,
  convertVariableNodesToText,
  convertVariableNodesToPlaceholders,
  resolvePlaceholders,
  findDisallowedSubjectVariables,
} from "@/lib/email-placeholder-utils";
import { useEmailMergeData, type EmailMergeDataResponse } from "@/hooks/useEmailMergeData";
import { useMode } from "@/providers/ModeProvider";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface EmailAttachment {
  base64Content: string;
  fileName: string;
  contentType: string;
}

export interface SingleEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Title shown in the dialog header (defaults to "Send Email") */
  title?: string;
  /** Customer ID to fetch contacts for */
  customerId: string;
  /** Display name for the customer */
  customerName: string;
  /** Account number for display */
  accountNumber?: string;
  /** Source type for server-side merge data resolution */
  sourceType: string;
  /** Source ID (e.g. promiseId, caseId, disputeId) */
  sourceId?: number;
  /** Transaction IDs to include in merge data request (for transaction-level resolution) */
  transactionIds?: number[];
  /** Callback event name the MFE fires on completion (defaults to "creditcontrol:single-email-done") */
  callbackEventName?: string;
  /** Attach invoices checkbox label (defaults to "Attach invoice copies") */
  attachLabel?: string;
  /** Optional async function to fetch file attachments (e.g. invoice PDFs).
   *  Called when user checks the attach checkbox.
   *  Should report progress via the provided callback. */
  fetchAttachments?: (onProgress: (done: number, total: number) => void) => Promise<EmailAttachment[]>;
  /** Called just before the MFE step opens (e.g. for logging the send action) */
  onBeforeSend?: (payload: { toEmails: string[]; ccEmails: string[]; subject: string }) => void;
  /** Success message shown via toast (defaults to "Email sent successfully") */
  successMessage?: string;
  /** Called when the MFE reports success */
  onSuccess?: () => void;
  /**
   * Optional list of allowed template category type codes (e.g. ["transactions", "general"]).
   * When provided, only templates whose category matches one of these codes are shown.
   * Templates with no category assigned are always included.
   */
  allowedCategoryTypes?: string[];
  /**
   * Optional list of allowed template type codes (e.g. ["email"] or ["statement"]).
   * When provided, only templates whose type matches one of these codes are shown.
   */
  allowedTemplateTypes?: string[];
  /** Called after the MFE confirms the email was sent successfully, with the fully resolved email payload */
  onAfterSend?: (payload: {
    toEmails: string[];
    ccEmails: string[];
    subject: string;
    htmlBody: string;
    templateId?: string;
    templateName?: string;
    isSubjectModified: boolean;
    isBodyModified: boolean;
    attachOutstandingInvoices: boolean;
  }) => void;
}

// ─── Main modal (shell) ─────────────────────────────────────────────────────

export function SingleEmailModal({
  open,
  onOpenChange,
  title = "Send Email",
  customerId,
  customerName,
  accountNumber,
  sourceType,
  sourceId,
  transactionIds,
  callbackEventName = "creditcontrol:single-email-done",
  attachLabel = "Attach invoice copies",
  fetchAttachments,
  onBeforeSend,
  successMessage,
  onSuccess,
  allowedCategoryTypes,
  allowedTemplateTypes,
  onAfterSend,
}: Readonly<SingleEmailModalProps>) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && (
        <SingleEmailModalContent
          onOpenChange={onOpenChange}
          title={title}
          customerId={customerId}
          customerName={customerName}
          accountNumber={accountNumber}
          sourceType={sourceType}
          sourceId={sourceId}
          transactionIds={transactionIds}
          callbackEventName={callbackEventName}
          attachLabel={attachLabel}
          fetchAttachments={fetchAttachments}
          onBeforeSend={onBeforeSend}
          successMessage={successMessage}
          onSuccess={onSuccess}
          allowedCategoryTypes={allowedCategoryTypes}
          allowedTemplateTypes={allowedTemplateTypes}
          onAfterSend={onAfterSend}
        />
      )}
    </Dialog>
  );
}

// ─── Modal content (unmounts on close to reset all state) ────────────────────

function SingleEmailModalContent({
  onOpenChange,
  title,
  customerId,
  customerName,
  sourceType,
  sourceId,
  transactionIds,
  callbackEventName,
  attachLabel,
  fetchAttachments,
  onBeforeSend,
  successMessage,
  onSuccess,
  allowedCategoryTypes,
  allowedTemplateTypes,
  onAfterSend,
}: Readonly<Omit<SingleEmailModalProps, "open">>) {
  const modalTitle = title ?? "Send Email";
  const eventName = callbackEventName ?? "creditcontrol:single-email-done";
  const attachCheckboxLabel = attachLabel ?? "Attach invoice copies";

  const { isDemo } = useMode();

  // ── Step ──────────────────────────────────────────────────────────────────
  const [step, setStep] = useState<1 | 2>(1);

  // ── Auth (for MFE instanceId) ────────────────────────────────────────────
  const { instanceId } = useAuth();

  // ── Step 1 state ─────────────────────────────────────────────────────────
  const [template, setTemplate] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [subjectHtml, setSubjectHtml] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachInvoices, setAttachInvoices] = useState(false);

  const originalTemplateSubject = useRef<string>("");
  const originalTemplateBody = useRef<string>("");
  const bodyEditorRef = useRef<Editor | null>(null);

  // Ref to store the resolved email payload for onAfterSend
  const sentEmailPayloadRef = useRef<{
    toEmails: string[];
    ccEmails: string[];
    subject: string;
    htmlBody: string;
    templateId?: string;
    templateName?: string;
    isSubjectModified: boolean;
    isBodyModified: boolean;
    attachOutstandingInvoices: boolean;
  } | null>(null);

  // ── Email merge data API ──────────────────────────────────────────────
  const mergeDataMutation = useEmailMergeData();
  const cachedMergeDataRef = useRef<EmailMergeDataResponse | null>(null);

  // ── Recipient state ────────────────────────────────────────────────────────
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const hasSeededRecipients = useRef(false);

  // ── MFE state (Step 2) ─────────────────────────────────────────────────────
  const [singleEmailParam, setSingleEmailParam] = useState<string>("");
  const [mfeDone, setMfeDone] = useState(false);

  // ── Ref for stable doFetchAttachments identity ─────────────────────────────
  const doFetchAttachmentsRef = useRef<() => Promise<void>>(async () => {});

  // ── Attachment state ─────────────────────────────────────────────────────
  const [fetchedAttachments, setFetchedAttachments] = useState<EmailAttachment[]>([]);
  const [isDownloadingAttachments, setIsDownloadingAttachments] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ done: 0, total: 0 });
  const [noInvoiceDialogOpen, setNoInvoiceDialogOpen] = useState(false);
  const [noInvoiceDialogMessage, setNoInvoiceDialogMessage] = useState("");

  // ── Data hooks ───────────────────────────────────────────────────────────
  const { data: templatesData } = useTemplatesList();
  const allTemplates = templatesData ?? [];
  const templates = allTemplates.filter((x) => {
    if (allowedCategoryTypes) {
      if (x.categoryTypeName && !allowedCategoryTypes.some((code) => x.categoryTypeName!.toLowerCase() === code.toLowerCase())) {
        return false;
      }
    }
    if (allowedTemplateTypes) {
      if (!allowedTemplateTypes.some((t) => x.type.toLowerCase() === t.toLowerCase())) {
        return false;
      }
    }
    return true;
  });
  const { data: templateDetail } = useTemplateById(selectedTemplateId);
  const { data: contacts = [], isLoading: isContactsLoading } = useCustomerContactsList(customerId);

  // ── TipTap subject editor ────────────────────────────────────────────────
  const subjectEditor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        bulletList: false,
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        horizontalRule: false,
      }),
      Placeholder.configure({ placeholder: "Email Subject" }),
      VariableExtension,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm max-w-none focus:outline-none p-3 min-h-[44px]",
      },
    },
    onUpdate: ({ editor }) => {
      setSubjectHtml(editor.getHTML());
    },
  });

  const handleBodyEditorReady = useCallback((editor: Editor) => {
    bodyEditorRef.current = editor;
  }, []);

  // ── Seed recipient emails once when contacts load ──────────────────────────
  useEffect(() => {
    if (!hasSeededRecipients.current && contacts.length > 0) {
      const primary = contacts.find((c) => c.isPrimary) || contacts[0];
      if (primary?.email) {
        setToEmails([primary.email]);
      }
      hasSeededRecipients.current = true;
    }
  }, [contacts]);

  // ── Fetch attachments (triggered immediately when checkbox is checked) ─────
  const doFetchAttachments = useCallback(async () => {
    if (!fetchAttachments) return;

    setIsDownloadingAttachments(true);
    setDownloadProgress({ done: 0, total: 0 });

    try {
      const results = await fetchAttachments((done, total) => {
        setDownloadProgress({ done, total });
      });

      if (results.length === 0) {
        setAttachInvoices(false);
        setFetchedAttachments([]);
        setNoInvoiceDialogMessage("No external invoice found. The email will be sent without invoice attachments.");
        setNoInvoiceDialogOpen(true);
      } else {
        setFetchedAttachments(results);
      }
    } catch {
      setAttachInvoices(false);
      setFetchedAttachments([]);
      setNoInvoiceDialogMessage("An error occurred while getting external invoices. The email will be sent without invoice attachments.");
      setNoInvoiceDialogOpen(true);
    } finally {
      setIsDownloadingAttachments(false);
    }
  }, [fetchAttachments]);

  useEffect(() => {
    doFetchAttachmentsRef.current = doFetchAttachments;
  }, [doFetchAttachments]);

  // ── Trigger fetch when checkbox is checked ─────────────────────────────────
  const handleAttachInvoicesChange = useCallback(
    (checked: boolean) => {
      setAttachInvoices(checked);
      if (checked && fetchAttachments) {
        doFetchAttachments();
      } else if (!checked) {
        setFetchedAttachments([]);
      }
    },
    [fetchAttachments, doFetchAttachments],
  );

  // ── Populate editors when template is selected ────────────────────────────
  useEffect(() => {
    if (!templateDetail) return;
    const subjectContent = templateDetail.subject || "";
    const bodyContent = templateDetail.content || "";

    originalTemplateSubject.current = subjectContent;
    originalTemplateBody.current = bodyContent;

    const convertedSubject = convertVariablesToNodes(subjectContent);
    if (subjectEditor && !subjectEditor.isDestroyed) {
      subjectEditor.commands.setContent(convertedSubject);
    }

    const convertedBody = convertVariablesToNodes(bodyContent);
    if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
      bodyEditorRef.current.commands.setContent(convertedBody);
    }
    setBodyHtml(convertedBody);

    const shouldAttach = templateDetail.attachInvoiceCopies ?? false;
    setAttachInvoices(shouldAttach);
    if (shouldAttach) {
      doFetchAttachmentsRef.current();
    } else {
      setFetchedAttachments([]);
    }
  }, [templateDetail, subjectEditor]);

  // ── Template change ──────────────────────────────────────────────────────
  const handleTemplateChange = (templateId: string) => {
    setTemplate(templateId);
    if (templateId === "none") {
      setSelectedTemplateId(null);
      if (subjectEditor && !subjectEditor.isDestroyed) {
        subjectEditor.commands.setContent("");
      }
      if (bodyEditorRef.current && !bodyEditorRef.current.isDestroyed) {
        bodyEditorRef.current.commands.setContent("");
      }
      setBodyHtml("");
      originalTemplateSubject.current = "";
      originalTemplateBody.current = "";
      setAttachInvoices(false);
      setFetchedAttachments([]);
    } else {
      setSelectedTemplateId(templateId);
    }
  };

  // ── Navigation ───────────────────────────────────────────────────────────
  // handleNext is no longer needed as step 1 goes directly to MFE via handleOpenMfe

  // ── Build merge data & open MFE ──────────────────────────────────────────
  // The SendIt MFE resolves <<key>> placeholders using the mergeData dictionary.
  const handleOpenMfe = useCallback(async () => {
    const currentSubjectForValidation = convertVariableNodesToText(subjectEditor?.getHTML() || subjectHtml);

    if (!currentSubjectForValidation.trim()) {
      toast.error("Please enter an email subject");
      return;
    }

    if (toEmails.length === 0) {
      toast.error("Please add at least one recipient");
      return;
    }
    const disallowedInSubject = findDisallowedSubjectVariables(currentSubjectForValidation);
    if (disallowedInSubject.length > 0) {
      toast.error(
        `The following variables cannot be used in the subject line because they produce complex HTML: ${disallowedInSubject.map((k) => `<<${k}>>`).join(", ")}. Please use them only in the email body.`,
      );
      return;
    }

    if (isDemo) {
      onBeforeSend?.({ toEmails, ccEmails, subject: convertVariableNodesToText(subjectEditor?.getHTML() || subjectHtml) });
      toast.success(successMessage ?? "Email sent successfully");
      onSuccess?.();
      onOpenChange(false);
      return;
    }

    // Resolve merge data before opening MFE
    const currentSubjectHtml = subjectEditor?.getHTML() || subjectHtml;
    const subjectPlain = convertVariableNodesToText(currentSubjectHtml);
    const bodyPlain = convertVariableNodesToPlaceholders(bodyHtml);

    try {
      const response = await mergeDataMutation.mutateAsync({
        sourceType,
        sourceId,
        templateId: selectedTemplateId ? parseInt(selectedTemplateId) : undefined,
        subject: subjectPlain,
        body: bodyPlain,
        recipients: [{
          recipientId: customerId,
          recipientEmail: toEmails[0] ?? "",
          customerId: parseInt(customerId),
          transactionIds,
        }],
      });

      cachedMergeDataRef.current = response;

      if (response.unknownPlaceholders.length > 0) {
        toast.error(
          "The template contains unknown variables that are not supported: " +
            response.unknownPlaceholders.map((k) => `<<${k}>>`).join(", ") +
            ". Please remove them or use variables from the Variables panel.",
        );
        return;
      }
      if (response.emptyDataPlaceholders.length > 0) {
        toast.error(
          "No data found for the following variables: " +
            response.emptyDataPlaceholders.map((k) => `<<${k}>>`).join(", ") +
            ". The associated records may be missing this information.",
        );
        return;
      }
    } catch {
      toast.error("Failed to build merge data. Please try again.");
      return;
    }

    const toEmail = toEmails[0];
    const cachedRecipient = cachedMergeDataRef.current?.recipients[0];
    const cachedMerge = cachedRecipient?.mergeData ?? {};
    const cachedMergeHtml = cachedRecipient?.mergeDataHtml ?? {};
    const mergeData: Record<string, string> = {};
    for (const [k, v] of Object.entries(cachedMerge)) {
      mergeData[k.toLowerCase()] = v;
    }
    // HTML fields (e.g. overdueTransactions, outstandingTransactions) are returned
    // in mergeDataHtml as raw HTML tables. Merge them into the resolution map so
    // resolvePlaceholders can substitute them. HTML values take precedence.
    for (const [k, v] of Object.entries(cachedMergeHtml)) {
      mergeData[k.toLowerCase()] = v;
    }
    mergeData["email"] = toEmail;

    const resolvedSubject = resolvePlaceholders(subjectPlain, mergeData);
    const resolvedBody = resolvePlaceholders(bodyPlain, mergeData);

    const allAttachments = fetchedAttachments.map((doc) => ({
        type: "base64" as const,
        filename: doc.fileName,
        mimeType: doc.contentType,
        data: doc.base64Content,
      }));

    const singlePayload = {
      to: toEmails,
      cc: ccEmails.length > 0 ? ccEmails : undefined,
      subject: resolvedSubject,
      body: resolvedBody,
      htmlBody: resolvedBody,
      attachments: allAttachments,
    };

    sentEmailPayloadRef.current = {
      toEmails,
      ccEmails,
      subject: resolvedSubject,
      htmlBody: resolvedBody,
      templateId: selectedTemplateId || undefined,
      templateName: templates.find(t => t.id === selectedTemplateId)?.name,
      isSubjectModified: selectedTemplateId ? (subjectEditor?.getHTML() || subjectHtml) !== originalTemplateSubject.current : false,
      isBodyModified: selectedTemplateId ? bodyHtml !== originalTemplateBody.current : false,
      attachOutstandingInvoices: attachInvoices,
    };

    setSingleEmailParam(JSON.stringify(singlePayload));
    setStep(2);

    onBeforeSend?.({ toEmails, ccEmails, subject: resolvedSubject });
  }, [isDemo, subjectEditor, subjectHtml, bodyHtml, sourceType, sourceId, selectedTemplateId, customerId, toEmails, ccEmails, transactionIds, mergeDataMutation, fetchedAttachments, attachInvoices, templates, onBeforeSend, successMessage, onSuccess, onOpenChange]);

  // ── Stable MFE parameters ───────────────────────────────────────────────
  const mfeParameters = useMemo(
    () =>
      singleEmailParam
        ? {
            mode: "single" as const,
            email: singleEmailParam,
            callbackEventName: eventName,
          }
        : undefined,
    [singleEmailParam, eventName],
  );

  // ── Listen for SendIt MFE callback ───────────────────────────────────
  useEffect(() => {
    if (step !== 2) return;

    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;

      // MFE Cancel / Back button — just close the dialog
      if (detail?.action === 'close') {
        onOpenChange(false);
        return;
      }

      setMfeDone(true);

      if (sentEmailPayloadRef.current) {
        onAfterSend?.(sentEmailPayloadRef.current);
        toast.success(successMessage ?? "Email sent successfully");
        onSuccess?.();
      }

      onOpenChange(false);
    };

    window.addEventListener(eventName, handler);
    return () => window.removeEventListener(eventName, handler);
  }, [step, eventName, onOpenChange, onAfterSend, onSuccess, successMessage]);

  // ── Step 1 render ────────────────────────────────────────────────────────
  if (step === 1) {
    return (
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
          <DialogDescription>
            Step 1 of 2 — Compose email for {customerName}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2 overflow-y-auto flex-1 min-h-0">
          {/* ── Recipients ─────────────────────────────────────────── */}

          {/* To emails */}
          <div className="grid gap-2">
            <Label>To *</Label>
            <CompactEmailSelector
              selectedEmails={toEmails}
              onEmailsChange={setToEmails}
              contacts={contacts}
              required
              loading={isContactsLoading}
              placeholder="No recipients yet"
            />
          </div>

          {/* CC emails */}
          <div className="grid gap-2">
            <Label>CC</Label>
            <CompactEmailSelector
              selectedEmails={ccEmails}
              onEmailsChange={setCcEmails}
              contacts={contacts}
              placeholder="No CC recipients"
            />
          </div>

          {/* Template */}
          <div className="grid gap-2">
            <Label htmlFor="single-template">Template (Optional)</Label>
            <Select value={template} onValueChange={handleTemplateChange}>
              <SelectTrigger id="single-template">
                <SelectValue placeholder="Select a template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {templates.map((t) => (
                  <SelectItem key={t.id} value={t.id}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject */}
          <div className="grid gap-2">
            <Label>Subject</Label>
            {subjectEditor ? (
              <div className="border rounded-md">
                <EditorContent
                  editor={subjectEditor}
                  className="prose prose-sm max-w-none [&_.ProseMirror]:p-3 [&_.ProseMirror]:min-h-[44px] [&_.ProseMirror]:focus:outline-none"
                />
              </div>
            ) : (
              <div className="border rounded-md p-3 min-h-[44px] text-muted-foreground">
                Loading editor...
              </div>
            )}
          </div>

          {/* Message */}
          <div className="grid gap-2">
            <Label>Message</Label>
            <RichTextEditor
              content={bodyHtml}
              onChange={setBodyHtml}
              onEditorReady={handleBodyEditorReady}
              placeholder="Enter your message..."
              showToolbar={false}
            />
          </div>

          {/* Attach invoices */}
          <div className="flex items-center space-x-2">
            <Checkbox
              id="single-attach-invoices"
              checked={attachInvoices}
              onCheckedChange={(checked) => handleAttachInvoicesChange(checked === true)}
            />
            <Label htmlFor="single-attach-invoices" className="text-sm font-normal">
              {attachCheckboxLabel}
            </Label>
          </div>

          {/* Attachment download progress (shown while fetching) */}
          {isDownloadingAttachments && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Getting external invoice{downloadProgress.total > 1 ? "s" : ""}
              {downloadProgress.total > 0 && (
                <> ({downloadProgress.done}/{downloadProgress.total})</>
              )}...
            </div>
          )}
          {!isDownloadingAttachments && fetchedAttachments.length > 0 && (
            <div className="text-sm text-muted-foreground">
              {fetchedAttachments.length} invoice document(s) ready to attach
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleOpenMfe}
            disabled={isDownloadingAttachments || mergeDataMutation.isPending || toEmails.length === 0}
          >
            {isDownloadingAttachments ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Getting external invoice...
              </>
            ) : mergeDataMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Loading…
              </>
            ) : (
              <>
                Next
                <ArrowRight className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </DialogFooter>

        {/* No invoice found / error alert dialog */}
        <AlertDialog open={noInvoiceDialogOpen} onOpenChange={setNoInvoiceDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-amber-500" />
                Invoice Attachment
              </AlertDialogTitle>
              <AlertDialogDescription>
                {noInvoiceDialogMessage}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogAction onClick={() => setNoInvoiceDialogOpen(false)}>
                OK
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    );
  }

  // ── Step 2 render (SendIt MFE) ───────────────────────────────────────────
  // Render only the MFE without our own header/footer — the SendIt MFE
  // provides its own stepper, Cancel, and Back controls.
  return (
    <DialogContent className="w-[95vw] max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto">
        {mfeParameters && !mfeDone && (
          <EvoMfeApp
            name="evo-platform-sendit-mfe"
            instanceId={instanceId}
            parameters={mfeParameters}
          />
        )}
      </div>
    </DialogContent>
  );
}
